import { useState } from "react";
import axios from "axios";
import "./Comments.css";

const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const EMOJIS = [
  "😀","😂","😍","🥰","😎","😭","😅","🤔","😱","🙌",
  "👏","❤️","🔥","✨","💯","🎉","👍","😊","🥲","😤",
  "😢","🤩","😋","🤗","😏","💪","🙏","👀","😴","🤯",
];

const COLORS = ["#8b5cf6","#06b6d4","#ec4899","#f59e0b","#10b981","#3b82f6","#ef4444"];

const makeAvatar = (name = "?") => {
  const initials = name.trim().split(/\s+/).map(w => w[0] || "").join("").toUpperCase().slice(0,2) || "?";
  const color    = COLORS[name.charCodeAt(0) % COLORS.length];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <circle cx="50" cy="50" r="50" fill="${color}"/>
    <text x="50" y="50" dominant-baseline="central" text-anchor="middle"
      fill="white" font-size="38" font-family="sans-serif" font-weight="700">${initials}</text>
  </svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const normalizeComment = (c) => ({
  ...c,
  likes:   Array.isArray(c.likes)   ? c.likes   : [],
  replies: Array.isArray(c.replies) ? c.replies : [],
});

// ── flatten nested replies into ONE flat list, Instagram-style ──
const flattenReplies = (replies) => {
  const out = [];
  const walk = (list) => {
    list.forEach(r => {
      out.push(r);
      if (Array.isArray(r.replies) && r.replies.length > 0) walk(r.replies);
    });
  };
  walk(replies || []);
  return out;
};

function Av({ src, name = "?", size = 32 }) {
  const [err, setErr] = useState(false);
  return (
    <img
      src={!src || err ? makeAvatar(name) : src}
      alt={name}
      onError={() => setErr(true)}
      style={{
        width: size, height: size, borderRadius: "50%",
        objectFit: "cover", objectPosition: "center",
        display: "block", flexShrink: 0,
        border: "2px solid #080b14", background: "#1a1040",
      }}
    />
  );
}

function EmojiPicker({ onPick, onClose }) {
  return (
    <div className="emoji-picker">
      {EMOJIS.map(e => (
        <button key={e} className="emoji-btn" type="button"
          onClick={() => { onPick(e); onClose(); }}>
          {e}
        </button>
      ))}
    </div>
  );
}

function InputRow({
  avatarSrc, avatarName, value, onChange,
  onSubmit, posting, placeholder = "Add a comment...", small = false
}) {
  const [showEmoji, setShowEmoji] = useState(false);
  return (
    <div className={`cmt-input-row ${small ? "cmt-input-row--small" : ""}`}>
      <div className="cmt-avatar-ring">
        <Av src={avatarSrc} name={avatarName} size={small ? 28 : 36} />
      </div>
      <div className="cmt-input-wrap">
        <input
          className="cmt-input"
          placeholder={placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); onSubmit(); } }}
        />
        <button
          className="cmt-emoji-trigger"
          onClick={() => setShowEmoji(p => !p)}
          type="button"
        >
          😊
        </button>
        {showEmoji && (
          <EmojiPicker
            onPick={em => onChange(value + em)}
            onClose={() => setShowEmoji(false)}
          />
        )}
      </div>
      <button
        className="cmt-post-btn"
        onClick={onSubmit}
        disabled={posting || !value.trim()}
        type="button"
      >
        {posting ? "..." : "Post"}
      </button>
    </div>
  );
}

// ── Single Reply — flat layout, owns its OWN reply input now ──
function ReplyItem({
  reply, commentId, currentUsername, currentUserPic,
  blogAuthor, onCommentUpdate, activeReplyTo, onSetReplyTo
}) {
  const [replyText, setReplyText] = useState("");
  const [posting, setPosting]     = useState(false);
  const [deleting, setDeleting]   = useState(false);

  const liked     = Array.isArray(reply.likes) ? reply.likes.includes(currentUsername) : false;
  const likeCount = Array.isArray(reply.likes) ? reply.likes.length : 0;
  const isReplyingHere = activeReplyTo === reply._id;

  const canDelete = () => {
    const norm = s => (s || "").trim().toLowerCase();
    return (
      norm(reply.userName) === norm(currentUsername) ||
      norm(blogAuthor)     === norm(currentUsername)
    );
  };

  const handleLike = async () => {
    try {
      const res = await axios.post(
        `${BASE_URL}/api/comments/${commentId}/reply/${reply._id}/like`,
        { userName: currentUsername }
      );
      if (res.data.success) onCommentUpdate(res.data.comment);
    } catch (err) { console.error(err); }
  };



  const handleReply = async () => {
    if (!replyText.trim()) return;
    setPosting(true);
    try {
      //const text = `${reply.userName} ${replyText.trim()}`;
      const res = await axios.post(
        `${BASE_URL}/api/comments/${commentId}/reply`,
        { userName: currentUsername, text: replyText.trim(), parentReplyId: reply._id }
      );
      if (res.data.success) {
        onCommentUpdate(res.data.comment);
        setReplyText("");
        onSetReplyTo(null);
      }
    } catch (err) { console.error(err); }
    finally { setPosting(false); }
  };



  const handleDelete = async () => {
    if (!window.confirm("Delete this reply?")) return;
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${BASE_URL}/api/comments/${commentId}/reply/${reply._id}`,
        { headers: { "Content-Type": "application/json" }, data: { userName: currentUsername } }
      );
      if (res.data.success) onCommentUpdate(res.data.comment);
    } catch (err) { console.error(err); }
    finally { setDeleting(false); }
  };

  return (
    <div className="reply-item">
      <div className="reply-main">
        <div className="cmt-avatar-ring">
          <Av
            src={reply.userName === currentUsername ? currentUserPic : null}
            name={reply.userName}
            size={28}
          />
        </div>
        <div className="reply-bubble">
          <span className="cmt-username">{reply.userName}</span>
          <span className="cmt-text">{reply.text}</span>
          <div className="reply-actions">
            <button
              className={`reply-like-btn ${liked ? "liked" : ""}`}
              onClick={handleLike}
              type="button"
            >
              <svg viewBox="0 0 24 24" className="reply-heart">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
              {likeCount > 0 && <span>{likeCount}</span>}
            </button>
            <button
              className="reply-action-text"
              onClick={() => onSetReplyTo(isReplyingHere ? null : reply._id)}
              type="button"
            >
              Reply
            </button>
            {canDelete() && (
              <button
                className="cmt-delete-btn"
                onClick={handleDelete}
                disabled={deleting}
                type="button"
              >
                {deleting ? "..." : (
                  <svg viewBox="0 0 24 24" className="cmt-delete-icon">
                    <path d="M3 6h18"/><path d="M8 6V4h8v2"/>
                    <path d="M19 6l-1 14H6L5 6"/>
                    <path d="M10 11v6"/><path d="M14 11v6"/>
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* reply input now sits directly below THIS reply, not the parent comment */}
      {isReplyingHere && (
        <div className="reply-input-wrap">
          <InputRow
            avatarSrc={currentUserPic}
            avatarName={currentUsername}
            value={replyText}
            onChange={setReplyText}
            onSubmit={handleReply}
            posting={posting}
            placeholder={`Reply to ${reply.userName}...`}
            small
          />
        </div>
      )}
    </div>
  );
}

// ── Single Comment ──
function CommentItem({
  comment, currentUsername, currentUserPic,
  blogAuthor, pics, onDelete, onCommentUpdate
}) {
  const [replyText, setReplyText]     = useState("");
  const [posting, setPosting]         = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [deleting, setDeleting]       = useState(false);

  // activeReplyTo: null = no reply box open anywhere in this thread
  //                "comment" = reply box open under the main comment
  //                <replyId> = reply box open under that specific reply
  const [activeReplyTo, setActiveReplyTo] = useState(null);

  const liked     = Array.isArray(comment.likes) ? comment.likes.includes(currentUsername) : false;
  const likeCount = Array.isArray(comment.likes) ? comment.likes.length : 0;

  const flatReplies = flattenReplies(comment.replies);
  const hasReplies  = flatReplies.length > 0;

  const canDelete = () => {
    const norm = s => (s || "").trim().toLowerCase();
    return (
      norm(comment.userName) === norm(currentUsername) ||
      norm(blogAuthor)       === norm(currentUsername)
    );
  };

  const handleLike = async () => {
    try {
      const res = await axios.post(
        `${BASE_URL}/api/comments/${comment._id}/like`,
        { userName: currentUsername }
      );
      if (res.data.success) {
        onCommentUpdate(comment._id, { likes: res.data.likes });
      }
    } catch (err) { console.error(err); }
  };

  const handleReplyToComment = async () => {
    if (!replyText.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/api/comments/${comment._id}/reply`,
        { userName: currentUsername, text: replyText.trim() }
      );
      if (res.data.success) {
        onCommentUpdate(comment._id, { replies: res.data.comment.replies });
        setReplyText("");
        setActiveReplyTo(null);
        setShowReplies(true);
      }
    } catch (err) { console.error(err); }
    finally { setPosting(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this comment?")) return;
    setDeleting(true);
    try {
      const res = await axios.delete(`${BASE_URL}/api/comments/${comment._id}`, {
        headers: { "Content-Type": "application/json" },
        data: { userName: currentUsername },
      });
      if (res.data?.success) onDelete(comment._id);
      else alert("Could not delete.");
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to delete.");
    } finally { setDeleting(false); }
  };

  // when any reply posts a sub-reply, the backend returns the full updated
  // comment — bubble it up to the parent Comments state the same way
  const handleReplyCommentUpdate = (updatedComment) => {
    onCommentUpdate(comment._id, { replies: updatedComment.replies });
    setShowReplies(true);
  };

  return (
    <div className="cmt-item">
      <div className="cmt-item-main">
        <div className="cmt-avatar-ring">
          <Av
            src={comment.userName === currentUsername
              ? currentUserPic
              : (pics[comment.userName] || null)}
            name={comment.userName}
            size={34}
          />
        </div>
        <div className="cmt-bubble">
          <span className="cmt-username">{comment.userName}</span>
          <span className="cmt-text">{comment.comment}</span>
          <div className="cmt-actions">
            <button
              className={`reply-like-btn ${liked ? "liked" : ""}`}
              onClick={handleLike}
              type="button"
            >
              <svg viewBox="0 0 24 24" className="reply-heart">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
              {likeCount > 0 && <span>{likeCount}</span>}
            </button>
            <button
              className="reply-action-text"
              onClick={() => setActiveReplyTo(activeReplyTo === "comment" ? null : "comment")}
              type="button"
            >
              Reply
            </button>
            {hasReplies && (
              <button
                className="reply-action-text"
                onClick={() => setShowReplies(p => !p)}
                type="button"
              >
                {showReplies
                  ? "Hide replies"
                  : `View ${flatReplies.length} repl${flatReplies.length === 1 ? "y" : "ies"}`}
              </button>
            )}
            {canDelete() && (
              <button
                className="cmt-delete-btn"
                onClick={handleDelete}
                disabled={deleting}
                type="button"
              >
                {deleting ? "..." : (
                  <svg viewBox="0 0 24 24" className="cmt-delete-icon">
                    <path d="M3 6h18"/><path d="M8 6V4h8v2"/>
                    <path d="M19 6l-1 14H6L5 6"/>
                    <path d="M10 11v6"/><path d="M14 11v6"/>
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* reply box for the MAIN comment — only shows here */}
      {activeReplyTo === "comment" && (
        <div className="reply-input-wrap">
          <InputRow
            avatarSrc={currentUserPic}
            avatarName={currentUsername}
            value={replyText}
            onChange={setReplyText}
            onSubmit={handleReplyToComment}
            posting={posting}
            placeholder={`Reply to ${comment.userName}...`}
            small
          />
        </div>
      )}

      {/* flat reply list — each reply owns its own reply-input slot */}
      {showReplies && hasReplies && (
        <div className="nested-replies">
          {flatReplies.map(r => (
            <ReplyItem
              key={r._id}
              reply={r}
              commentId={comment._id}
              currentUsername={currentUsername}
              currentUserPic={currentUserPic}
              blogAuthor={blogAuthor}
              onCommentUpdate={handleReplyCommentUpdate}
              activeReplyTo={activeReplyTo}
              onSetReplyTo={setActiveReplyTo}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// MAIN Comments component
// ─────────────────────────────────────────
// `onCommentPosted` is OPTIONAL and additive. When it is not supplied — which
// is every existing caller, all of them free blog screens — this component
// behaves exactly as it always has. The Pro blog card passes one so it can tell
// the server a Pro comment was posted and have a Pro notification raised
// (Step 20); the free comment endpoints know nothing about Pro and are left
// untouched. It is called only AFTER the comment has actually been saved, and
// its failure can never affect the comment.
function Comments({ blogId, blogAuthor, currentUsername, currentUserPic, isOpen, onCommentPosted }) {
  const [comments, setComments]       = useState(null);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting]         = useState(false);
  const [pics, setPics]               = useState({});
  const [loaded, setLoaded]           = useState(false);

  const fetchPic = async (name) => {
    if (!name || pics[name] !== undefined) return;
    setPics(prev => ({ ...prev, [name]: null }));
    try {
      const res = await axios.get(
        `${BASE_URL}/api/profile/username/${encodeURIComponent(name)}`
      );
      setPics(prev => ({ ...prev, [name]: res.data.profilePic || "" }));
    } catch {
      setPics(prev => ({ ...prev, [name]: "" }));
    }
  };

  const loadComments = async () => {
    if (loaded) return;
    try {
      const res = await axios.get(`${BASE_URL}/api/comments/${blogId}`);
      const normalized = res.data.map(normalizeComment);
      setComments(normalized);
      normalized.forEach(c => fetchPic(c.userName));
      setLoaded(true);
    } catch (err) {
      console.error("loadComments error:", err);
      setComments([]);
      setLoaded(true);
    }
  };

  if (isOpen && !loaded) loadComments();

  const submitComment = async () => {
    if (!commentText.trim()) return;
    if (!currentUsername) { alert("Please log in to comment."); return; }
    if (!blogId)           { alert("Blog ID missing.");          return; }

    setPosting(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/comments`, {
        postId:   blogId,
        userName: currentUsername,
        comment:  commentText.trim(),
      });
      const normalized = normalizeComment(res.data);
      setComments(prev => [normalized, ...(prev || [])]);
      setCommentText("");

      // Optional, and deliberately not awaited: the comment is already saved,
      // so nothing about it may depend on this succeeding.
      try {
        onCommentPosted?.();
      } catch {
        /* a caller's hook must never break commenting */
      }
    } catch (err) {
      console.error("submitComment error:", err.response?.data || err.message);
      alert(err.response?.data?.msg || "Failed to post comment.");
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = (commentId) => {
    setComments(prev => (prev || []).filter(c => c._id !== commentId));
  };

  const handleCommentUpdate = (commentId, patch) => {
    setComments(prev =>
      (prev || []).map(c => c._id === commentId ? { ...c, ...patch } : c)
    );
  };

  if (!isOpen) return null;

  return (
    <div className="cmt-box">
      <InputRow
        avatarSrc={currentUserPic}
        avatarName={currentUsername}
        value={commentText}
        onChange={setCommentText}
        onSubmit={submitComment}
        posting={posting}
        placeholder="Add a comment..."
      />

      <div className="cmt-list">
        {comments === null ? (
          <p className="cmt-empty">Loading...</p>
        ) : comments.length === 0 ? (
          <p className="cmt-empty">No comments yet 💬</p>
        ) : (
          comments.map(c => (
            <CommentItem
              key={c._id}
              comment={c}
              currentUsername={currentUsername}
              currentUserPic={currentUserPic}
              blogAuthor={blogAuthor}
              pics={pics}
              onDelete={handleDelete}
              onCommentUpdate={handleCommentUpdate}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default Comments;